import {
  analysisKeys,
  heatmapKeys,
  offlineSyncApi,
} from '@lidea/shared/data-access/analysis';
import { Heatmap } from '@lidea/shared/util/types';
import { isClientError } from '@lidea/shared/data-access/core';
import { plotKeys } from '@lidea/shared/data-access/plots';
import { PlotFeature, PlotPageResult, ZoningStatus } from '@lidea/shared/util/types';
import { QueryClient } from '@tanstack/react-query';
import pLimit from 'p-limit';

import { applyHeatmapThreshold } from './create-sync-job';
import { markOfflineSyncDone, saveSyncJob } from './sync-job-store';
import {
  CHUNK_SIZE,
  MAX_SYNC_ERRORS,
  SyncErrorEntry,
  SyncJob,
  SyncPhase,
} from './types';

export type RunSyncJobCallbacks = {
  onJobUpdate?: (job: SyncJob) => void;
  shouldAbort?: () => boolean;
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function pushError(
  job: SyncJob,
  entry: Omit<SyncErrorEntry, 'at'>
): SyncJob {
  const errors = [
    ...job.errors,
    { ...entry, at: Date.now() },
  ].slice(-MAX_SYNC_ERRORS);
  return { ...job, errors };
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

async function fetchWithRetry<T>(
  fn: () => Promise<T>,
  maxAttempts = 3
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (e) {
      lastError = e;
      if (isClientError(e instanceof Error ? e : null)) {
        throw e;
      }
      if (attempt < maxAttempts - 1) {
        await sleep(500 * (attempt + 1));
      }
    }
  }
  throw lastError;
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

function heatmapTaskKey(plotId: number, imageryIndex: string): string {
  return `${plotId}:${imageryIndex}`;
}

function parseHeatmapTaskKey(key: string): { plotId: number; imageryIndex: string } {
  const [plotId, ...rest] = key.split(':');
  return { plotId: Number(plotId), imageryIndex: rest.join(':') };
}

function seedPlotHeatmapsCache(
  queryClient: QueryClient,
  plotId: number,
  imageryIndex: string,
  data: Heatmap[]
) {
  queryClient.setQueryData(
    heatmapKeys.plotHeatmaps({ plotId, imageryIndex }).queryKey,
    data
  );
}

type HeatmapTaskResult = {
  taskKey: string;
  success: boolean;
  error?: Omit<SyncErrorEntry, 'at'>;
};

async function fetchHeatmapTasksIndividually(
  queryClient: QueryClient,
  taskKeys: string[],
  limit: ReturnType<typeof pLimit>
): Promise<HeatmapTaskResult[]> {
  return Promise.all(
    taskKeys.map((taskKey) =>
      limit(async (): Promise<HeatmapTaskResult> => {
        const { plotId, imageryIndex } = parseHeatmapTaskKey(taskKey);
        const opts = heatmapKeys.plotHeatmaps({ plotId, imageryIndex });
        if (queryClient.getQueryData(opts.queryKey)) {
          return { taskKey, success: true };
        }
        try {
          await fetchWithRetry(() => queryClient.fetchQuery(opts));
          return { taskKey, success: true };
        } catch (e) {
          return {
            taskKey,
            success: false,
            error: {
              phase: 'heatmaps',
              entityType: 'heatmap',
              entityId: taskKey,
              message: errorMessage(e),
            },
          };
        }
      })
    )
  );
}

async function fetchHeatmapChunkBulk(
  queryClient: QueryClient,
  job: SyncJob,
  taskKeys: string[]
): Promise<HeatmapTaskResult[]> {
  const uncached = taskKeys.filter((taskKey) => {
    const { plotId, imageryIndex } = parseHeatmapTaskKey(taskKey);
    return !queryClient.getQueryData(
      heatmapKeys.plotHeatmaps({ plotId, imageryIndex }).queryKey
    );
  });
  const cachedResults: HeatmapTaskResult[] = taskKeys
    .filter((k) => !uncached.includes(k))
    .map((taskKey) => ({ taskKey, success: true }));

  if (uncached.length === 0) {
    return cachedResults;
  }

  const plotIds = [...new Set(uncached.map((k) => parseHeatmapTaskKey(k).plotId))];
  const types = [...new Set(uncached.map((k) => parseHeatmapTaskKey(k).imageryIndex))];

  const bulk = await fetchWithRetry(() =>
    offlineSyncApi.getBulkHeatmaps({
      plotIds,
      types,
      filters: job.scope.filters,
    })
  );

  const fetched: HeatmapTaskResult[] = [];
  for (const taskKey of uncached) {
    const { plotId, imageryIndex } = parseHeatmapTaskKey(taskKey);
    const data = bulk.plots[String(plotId)]?.[imageryIndex] ?? [];
    seedPlotHeatmapsCache(queryClient, plotId, imageryIndex, data);
    fetched.push({ taskKey, success: true });
  }
  return [...cachedResults, ...fetched];
}

function seedPlotsInfiniteListCache(
  queryClient: QueryClient,
  campaignId: number,
  filters: SyncJob['scope']['filters'],
  pages: PlotPageResult[]
) {
  if (pages.length === 0) {
    return;
  }
  queryClient.setQueryData(plotKeys.search(filters, campaignId).queryKey, {
    pages,
    pageParams: pages.map((_, index) => index + 1),
  });
}

/** Rebuild list infinite-query cache from paginated pages (e.g. after app restart). */
export function reseedPlotsListFromSyncJob(
  queryClient: QueryClient,
  job: SyncJob
): void {
  const { campaignId, filters } = job.scope;
  const pageSize = 25;
  const pagesCount = Math.max(1, Math.ceil(job.totals.plots / pageSize));
  const pages: PlotPageResult[] = [];

  for (let page = 1; page <= pagesCount; page++) {
    const data = queryClient.getQueryData<PlotPageResult>(
      plotKeys.paginated({ filters, page, campaignId }).queryKey
    );
    if (data) {
      pages.push(data);
    }
  }

  seedPlotsInfiniteListCache(queryClient, campaignId, filters, pages);
}

async function persist(
  job: SyncJob,
  callbacks?: RunSyncJobCallbacks
): Promise<SyncJob> {
  await saveSyncJob(job);
  callbacks?.onJobUpdate?.(job);
  return job;
}

async function waitIfPaused(job: SyncJob): Promise<SyncJob> {
  let current = job;
  while (current.status === 'paused') {
    await sleep(400);
    const stored = await import('./sync-job-store').then((m) => m.getSyncJob());
    if (!stored || stored.id !== job.id) {
      return current;
    }
    current = stored;
  }
  return current;
}

export async function runListingPhase(
  queryClient: QueryClient,
  job: SyncJob,
  callbacks?: RunSyncJobCallbacks
): Promise<SyncJob> {
  let current: SyncJob = { ...job, status: 'listing' };
  await persist(current, callbacks);

  const filters = current.scope.filters;
  const campaignId = current.scope.campaignId;

  try {
    const firstPage = await fetchWithRetry(() =>
      queryClient.fetchQuery({
        ...plotKeys.paginated({ filters, page: 1, campaignId }),
      })
    );

    const pageSize = 25;
    const pagesCount = Math.ceil(firstPage.count / pageSize);
    const plotIds: number[] = firstPage.results.features.map((f) => f.id);
    const listPages: PlotPageResult[] = [firstPage];

    for (let page = 2; page <= pagesCount; page++) {
      if (callbacks?.shouldAbort?.()) {
        return persist({ ...current, status: 'paused' }, callbacks);
      }
      current = await waitIfPaused(current);
      const pageResult = await fetchWithRetry(() =>
        queryClient.fetchQuery({
          ...plotKeys.paginated({ filters, page, campaignId }),
        })
      );
      listPages.push(pageResult);
      plotIds.push(...pageResult.results.features.map((f) => f.id));
    }

    seedPlotsInfiniteListCache(queryClient, campaignId, filters, listPages);

    current = {
      ...current,
      status: 'plots',
      plotIds,
      pendingPlotIds: [...plotIds],
      totals: {
        ...current.totals,
        plots: plotIds.length,
      },
    };
    current = applyHeatmapThreshold(current);
    return persist(current, callbacks);
  } catch (e) {
    current = pushError(current, {
      phase: 'listing',
      entityType: 'listing',
      entityId: 'listing',
      message: errorMessage(e),
    });
    return persist({ ...current, status: 'failed' }, callbacks);
  }
}

type PlotChunkResult = {
  plotId: number;
  plot?: PlotFeature;
  error?: SyncErrorEntry;
  success: boolean;
};

async function fetchPlotForChunk(
  queryClient: QueryClient,
  campaignId: number,
  plotId: number
): Promise<PlotChunkResult> {
  const detailKey = plotKeys.detail(plotId, campaignId).queryKey;
  const cached = queryClient.getQueryData<PlotFeature>(detailKey);
  if (cached) {
    return { plotId, plot: cached, success: true };
  }
  try {
    const plot = await fetchWithRetry(() =>
      queryClient.fetchQuery(plotKeys.detail(plotId, campaignId))
    );
    return { plotId, plot, success: true };
  } catch (e) {
    return {
      plotId,
      success: false,
      error: {
        at: Date.now(),
        phase: 'plots',
        entityType: 'plot',
        entityId: String(plotId),
        message: errorMessage(e),
      },
    };
  }
}

export async function runPlotsPhase(
  queryClient: QueryClient,
  job: SyncJob,
  callbacks?: RunSyncJobCallbacks
): Promise<{ job: SyncJob; plots: PlotFeature[] }> {
  let current: SyncJob = { ...job, status: 'plots' };
  const limit = pLimit(current.options.concurrency);
  const fetchedPlots: PlotFeature[] = [];
  const chunkSize = current.options.chunkSize || CHUNK_SIZE;

  const chunks = chunkArray(current.pendingPlotIds, chunkSize);

  for (const chunk of chunks) {
    if (callbacks?.shouldAbort?.()) {
      await persist({ ...current, status: 'paused' }, callbacks);
      return { job: current, plots: fetchedPlots };
    }
    current = await waitIfPaused(current);

    const results = await Promise.all(
      chunk.map((plotId) =>
        limit(() =>
          fetchPlotForChunk(
            queryClient,
            current.scope.campaignId,
            plotId
          )
        )
      )
    );

    let successCount = 0;
    for (const r of results) {
      if (r.plot) {
        fetchedPlots.push(r.plot);
      }
      if (r.success) {
        successCount++;
      }
      if (r.error) {
        current = pushError(current, {
          phase: r.error.phase,
          entityType: r.error.entityType,
          entityId: r.error.entityId,
          message: r.error.message,
        });
      }
    }

    const doneIds = new Set(results.map((r) => r.plotId));
    current = {
      ...current,
      pendingPlotIds: current.pendingPlotIds.filter((id) => !doneIds.has(id)),
      completedPlots: current.completedPlots + successCount,
    };
    current = await persist(current, callbacks);
  }

  const analysisIdSet = new Set<number>();
  for (const plotId of current.plotIds) {
    const plot =
      queryClient.getQueryData<PlotFeature>(
        plotKeys.detail(plotId, current.scope.campaignId).queryKey
      ) ?? fetchedPlots.find((p) => p.id === plotId);
    if (!plot) {
      continue;
    }
    for (const analysis of plot.properties.analyzes) {
      if (analysis.status !== ZoningStatus.ToBeDone) {
        analysisIdSet.add(analysis.id);
      }
    }
  }
  const analysisIds = Array.from(analysisIdSet);

  current = {
    ...current,
    status: 'analyses',
    pendingAnalysisIds: analysisIds,
    totals: {
      ...current.totals,
      analyses: analysisIds.length,
    },
  };
  current = await persist(current, callbacks);
  return { job: current, plots: fetchedPlots };
}

export async function runAnalysesPhase(
  queryClient: QueryClient,
  job: SyncJob,
  callbacks?: RunSyncJobCallbacks
): Promise<SyncJob> {
  let current: SyncJob = { ...job, status: 'analyses' };
  const limit = pLimit(current.options.concurrency);
  const chunkSize = current.options.chunkSize || CHUNK_SIZE;
  const chunks = chunkArray(current.pendingAnalysisIds, chunkSize);

  for (const chunk of chunks) {
    if (callbacks?.shouldAbort?.()) {
      return persist({ ...current, status: 'paused' }, callbacks);
    }
    current = await waitIfPaused(current);

    const analysisResults = await Promise.all(
      chunk.map((analysisId) =>
        limit(async (): Promise<{
          analysisId: number;
          success: boolean;
          errors: Omit<SyncErrorEntry, 'at'>[];
        }> => {
          const errors: Omit<SyncErrorEntry, 'at'>[] = [];
          let failed = false;
          const zoningKey = analysisKeys.zoning(analysisId).queryKey;
          const measuresKey = analysisKeys.measures(analysisId).queryKey;

          if (!queryClient.getQueryData(zoningKey)) {
            try {
              await fetchWithRetry(() =>
                queryClient.fetchQuery(analysisKeys.zoning(analysisId))
              );
            } catch (e) {
              failed = true;
              errors.push({
                phase: 'analyses',
                entityType: 'analysis',
                entityId: `${analysisId}:zoning`,
                message: errorMessage(e),
              });
            }
          }

          if (!failed && !queryClient.getQueryData(measuresKey)) {
            try {
              await fetchWithRetry(() =>
                queryClient.fetchQuery(analysisKeys.measures(analysisId))
              );
            } catch (e) {
              failed = true;
              errors.push({
                phase: 'analyses',
                entityType: 'analysis',
                entityId: `${analysisId}:measures`,
                message: errorMessage(e),
              });
            }
          }

          return { analysisId, success: !failed, errors };
        })
      )
    );

    let analysisSuccessCount = 0;
    for (const r of analysisResults) {
      for (const err of r.errors) {
        current = pushError(current, err);
      }
      if (r.success) {
        analysisSuccessCount++;
      }
    }
    const doneAnalysisIds = new Set(
      analysisResults.map((r) => r.analysisId)
    );
    current = {
      ...current,
      pendingAnalysisIds: current.pendingAnalysisIds.filter(
        (id) => !doneAnalysisIds.has(id)
      ),
      completedAnalyses: current.completedAnalyses + analysisSuccessCount,
    };

    current = await persist(current, callbacks);
  }

  if (!current.options.includeHeatmaps) {
    return finalizeJob(queryClient, current, callbacks);
  }

  try {
    const imageryIndexes = await fetchWithRetry(() =>
      queryClient.fetchQuery(heatmapKeys.imageryIndexes())
    );
    const indexes = imageryIndexes.list_indexes;
    const tasks: string[] = [];
    for (const plotId of current.plotIds) {
      for (const im of indexes) {
        tasks.push(heatmapTaskKey(plotId, im));
      }
    }
    current = {
      ...current,
      status: 'heatmaps',
      imageryIndexes: indexes,
      pendingHeatmapTasks: tasks,
      totals: { ...current.totals, heatmapTasks: tasks.length },
    };
    return persist(current, callbacks);
  } catch (e) {
    current = pushError(current, {
      phase: 'heatmaps',
      entityType: 'heatmap',
      entityId: 'imageryIndexes',
      message: errorMessage(e),
    });
    return finalizeJob(queryClient, current, callbacks);
  }
}

export async function runHeatmapsPhase(
  queryClient: QueryClient,
  job: SyncJob,
  callbacks?: RunSyncJobCallbacks
): Promise<SyncJob> {
  let current: SyncJob = { ...job, status: 'heatmaps' };
  const limit = pLimit(current.options.concurrency);
  const chunkSize = current.options.chunkSize || CHUNK_SIZE;
  const chunks = chunkArray(current.pendingHeatmapTasks, chunkSize);

  for (const chunk of chunks) {
    if (callbacks?.shouldAbort?.()) {
      return persist({ ...current, status: 'paused' }, callbacks);
    }
    current = await waitIfPaused(current);

    let heatmapResults: HeatmapTaskResult[];
    try {
      heatmapResults = await fetchHeatmapChunkBulk(
        queryClient,
        current,
        chunk
      );
    } catch (bulkError) {
      current = pushError(current, {
        phase: 'heatmaps',
        entityType: 'heatmap',
        entityId: 'bulk',
        message: errorMessage(bulkError),
      });
      heatmapResults = await fetchHeatmapTasksIndividually(
        queryClient,
        chunk,
        limit
      );
    }

    let heatmapSuccess = 0;
    for (const r of heatmapResults) {
      if (r.success) {
        heatmapSuccess++;
      }
      if ('error' in r && r.error) {
        current = pushError(current, r.error);
      }
    }
    const doneTasks = new Set(heatmapResults.map((r) => r.taskKey));
    current = {
      ...current,
      pendingHeatmapTasks: current.pendingHeatmapTasks.filter(
        (t) => !doneTasks.has(t)
      ),
      completedHeatmapTasks: current.completedHeatmapTasks + heatmapSuccess,
    };

    current = await persist(current, callbacks);
  }

  return finalizeJob(queryClient, current, callbacks);
}

function finalizeJob(
  queryClient: QueryClient,
  job: SyncJob,
  callbacks?: RunSyncJobCallbacks
): Promise<SyncJob> {
  const hasPending =
    job.pendingPlotIds.length > 0 ||
    job.pendingAnalysisIds.length > 0 ||
    job.pendingHeatmapTasks.length > 0;

  let status: SyncPhase = 'completed';
  if (hasPending && job.errors.length > 0) {
    status = 'failed';
  } else if (job.errors.length > 0) {
    status = 'completed_with_errors';
  } else if (
    job.completedPlots === 0 &&
    job.totals.plots > 0 &&
    job.errors.length > 0
  ) {
    status = 'failed';
  }

  const finalized: SyncJob = {
    ...job,
    status,
    pendingPlotIds: [],
    pendingAnalysisIds: [],
    pendingHeatmapTasks: [],
  };

  if (status === 'completed' || status === 'completed_with_errors') {
    markOfflineSyncDone(
      finalized.scope.campaignId,
      finalized.scope.managerId,
      {
        plots: finalized.totals.plots,
        analyses: finalized.totals.analyses,
        includeHeatmaps: finalized.options.includeHeatmaps,
      }
    );
    if (finalized.totals.plots > 0) {
      reseedPlotsListFromSyncJob(queryClient, finalized);
    }
  }

  return persist(finalized, callbacks);
}

function collectAnalysisIds(
  queryClient: QueryClient,
  job: SyncJob
): number[] {
  const analysisIdSet = new Set<number>();
  for (const plotId of job.plotIds) {
    const plot = queryClient.getQueryData<PlotFeature>(
      plotKeys.detail(plotId, job.scope.campaignId).queryKey
    );
    if (!plot) {
      continue;
    }
    for (const analysis of plot.properties.analyzes) {
      if (analysis.status !== ZoningStatus.ToBeDone) {
        analysisIdSet.add(analysis.id);
      }
    }
  }
  return Array.from(analysisIdSet);
}

function ensureAnalysesQueue(
  queryClient: QueryClient,
  job: SyncJob
): SyncJob {
  if (job.pendingAnalysisIds.length > 0 || job.totals.analyses > 0) {
    return job;
  }
  const analysisIds = collectAnalysisIds(queryClient, job);
  return {
    ...job,
    status: 'analyses',
    pendingAnalysisIds: analysisIds,
    totals: { ...job.totals, analyses: analysisIds.length },
  };
}

export async function runSyncJob(
  queryClient: QueryClient,
  initialJob: SyncJob,
  callbacks?: RunSyncJobCallbacks
): Promise<SyncJob> {
  let job =
    initialJob.status === 'paused'
      ? { ...initialJob, status: inferResumePhase(initialJob) }
      : initialJob;

  const terminal = (j: SyncJob) =>
    j.status === 'failed' ||
    j.status === 'paused' ||
    j.status === 'completed' ||
    j.status === 'completed_with_errors';

  for (let guard = 0; guard < 8; guard++) {
    if (job.status === 'listing') {
      job = await runListingPhase(queryClient, job, callbacks);
      if (terminal(job)) {
        return job;
      }
      continue;
    }

    if (job.status === 'plots') {
      const { job: afterPlots } = await runPlotsPhase(
        queryClient,
        job,
        callbacks
      );
      job = afterPlots;
      if (terminal(job)) {
        return job;
      }
      continue;
    }

    if (job.status === 'analyses') {
      job = ensureAnalysesQueue(queryClient, job);
      job = await runAnalysesPhase(queryClient, job, callbacks);
      if (terminal(job)) {
        return job;
      }
      continue;
    }

    if (job.status === 'heatmaps') {
      job = await runHeatmapsPhase(queryClient, job, callbacks);
      return job;
    }

    return job;
  }

  return job;
}

/** Resume an interrupted job from its current phase. */
export async function resumeSyncJob(
  queryClient: QueryClient,
  callbacks?: RunSyncJobCallbacks
): Promise<SyncJob | undefined> {
  const job = await import('./sync-job-store').then((m) => m.getSyncJob());
  if (!job) {
    return undefined;
  }

  const resumable =
    job.status === 'paused' ||
    job.status === 'listing' ||
    job.status === 'plots' ||
    job.status === 'analyses' ||
    job.status === 'heatmaps';

  if (!resumable) {
    return job;
  }

  const activeJob =
    job.status === 'paused'
      ? { ...job, status: inferResumePhase(job) }
      : job;

  return runSyncJob(queryClient, activeJob, callbacks);
}

export function inferResumePhase(job: SyncJob): SyncPhase {
  if (job.plotIds.length === 0 && job.totals.plots === 0) {
    return 'listing';
  }
  if (job.pendingPlotIds.length > 0) {
    return 'plots';
  }
  if (job.pendingHeatmapTasks.length > 0) {
    return 'heatmaps';
  }
  if (job.pendingAnalysisIds.length > 0) {
    return 'analyses';
  }
  if (
    job.totals.analyses > 0 &&
    job.completedAnalyses < job.totals.analyses
  ) {
    return 'analyses';
  }
  if (job.options.includeHeatmaps && job.totals.heatmapTasks > 0) {
    return 'heatmaps';
  }
  if (job.plotIds.length > 0) {
    return 'analyses';
  }
  return 'listing';
}
